// A number line (13 Oct 2026): a line from `min` to `max` with a tick at
// every `step`, a number under every `labelEvery`-th tick, and lettered
// points sitting on it. The step may be a whole number, a decimal or a
// fraction written as text ("1/8").
//
// Pure parsing only, no components (see practice-diagram-data.ts).
//
// Nothing here is worked out in floating point. Every position is kept as a
// whole number of equal parts, with `parts` of them to each 1: on a line
// counted in hundredths 0.35 is 35, on a fourteenths line 5/14 is 5. So a
// tick at 0.3 or at 7/14 lands exactly, and two ways of writing the same
// place always agree.
//
// Year 8 (2 Nov 2026): a line may also show an inequality, as a ray (one
// dot, open or filled, and a thick arrow from it to one end of the line) or
// a segment (two dots, each open or filled, joined by a thick line). The
// dots sit on ticks. Lines without them are read and drawn as before.

import { spokenFraction } from '../../components/spokenFraction';

export type NumberLineSpec = {
  type: 'number_line';
  // How many equal parts make 1: a power of ten on a whole-number or decimal
  // line, the step's own bottom number on a fraction line.
  parts: number;
  // The step was written as a fraction. The line's numbers are then written
  // as fractions with that bottom number ("4/8", never "1/2").
  fraction: boolean;
  // All counted in parts.
  min: number;
  max: number;
  step: number;
  // Every tick has its number when this is 1.
  labelEvery: number;
  marks: { at: number; label: string }[];
  // Year 8: at most one of these, never both.
  ray?: NumberLineRay;
  segment?: NumberLineSegment;
};

// One end of an inequality: a ring (open) or a solid dot (filled), on a tick.
export type NumberLineDot = { at: number; dot: 'open' | 'filled' };
// A dot and a thick arrow from it to the left or right end of the line.
export type NumberLineRay = NumberLineDot & { arrow: 'left' | 'right' };
// Two dots, the left one first, joined by a thick line.
export type NumberLineSegment = { from: NumberLineDot; to: NumberLineDot };

// More gaps than this cannot be told apart on a phone.
const MAX_GAPS = 60;
const MAX_MARKS = 8;

// A number exactly as it was written: `top` over `bottom`.
type Exact = { top: number; bottom: number; fraction: boolean };

// A whole number, a plain decimal (as a number or as text) or a fraction
// written as text ("5/14"). Read from the digits, never by dividing. Null
// for anything else, such as a number too small to be written plainly.
function readExact(value: unknown): Exact | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const text = String(value).trim().replace('−', '-');
  const fraction = /^(-)?(\d{1,6})\/(\d{1,4})$/.exec(text);
  if (fraction) {
    const [, sign, top = '', bottom = ''] = fraction;
    if (Number(bottom) === 0) return null;
    return {
      top: sign ? -Number(top) : Number(top),
      bottom: Number(bottom),
      fraction: true,
    };
  }
  const decimal = /^(-)?(\d{1,9})(?:\.(\d{1,6}))?$/.exec(text);
  if (!decimal) return null;
  const [, sign, whole = '', places = ''] = decimal;
  const top = Number(whole + places);
  return {
    top: sign ? -top : top,
    bottom: 10 ** places.length,
    fraction: false,
  };
}

// The same number counted in parts. Null when it does not fall on a whole
// number of them (0.5 on a line counted in thirds).
function toParts(value: Exact, parts: number): number | null {
  const scaled = value.top * parts;
  if (!Number.isSafeInteger(scaled) || scaled % value.bottom !== 0) return null;
  return scaled / value.bottom;
}

// Where a number sits on a line, counted in the line's parts. Null when it
// is not a number, or cannot be written in those parts. On a whole-number
// or decimal line a fraction written as text is refused.
export function numberLineParts(
  line: Pick<NumberLineSpec, 'parts' | 'fraction'>,
  value: unknown,
): number | null {
  const exact = readExact(value);
  if (exact === null || (exact.fraction && !line.fraction)) return null;
  return toParts(exact, line.parts);
}

export function numberLineTickCount(line: NumberLineSpec): number {
  return (line.max - line.min) / line.step + 1;
}

// A place on a whole-number or decimal line in plain digits, built from the
// whole number of parts: "0.35", "2", "-3". No trailing zeros.
export function numberLineDecimal(
  line: Pick<NumberLineSpec, 'parts'>,
  at: number,
): string {
  const places = String(line.parts).length - 1;
  const digits = String(Math.abs(at)).padStart(places + 1, '0');
  const whole = digits.slice(0, digits.length - places);
  const after = digits.slice(digits.length - places).replace(/0+$/, '');
  const body = after.length > 0 ? `${whole}.${after}` : whole;
  return at < 0 ? `-${body}` : body;
}

// What a screen reader says for a place on the line, on the line's own
// terms: "0.35", "negative 3", "5 eighths", and a whole number on a
// fraction line as that whole number.
export function numberLineSpoken(
  line: Pick<NumberLineSpec, 'parts' | 'fraction'>,
  at: number,
): string {
  const size = Math.abs(at);
  const words = !line.fraction
    ? numberLineDecimal(line, size)
    : size % line.parts === 0
      ? String(size / line.parts)
      : spokenFraction(size, line.parts);
  return at < 0 ? `negative ${words}` : words;
}

// How a screen reader names a marked point. A letter is a name ("Point A");
// a dot or a question mark is not, and read aloud it is lost or garbled.
export function numberLineMarkSpoken(label: string): string {
  if (label === '•') return 'The dot';
  if (label === '?') return 'The unknown point';
  return `Point ${label}`;
}

// "1st", "2nd", "3rd", "4th", "11th", "21st".
function ordinal(count: number): string {
  const tens = count % 100;
  const ones = count % 10;
  const suffix =
    tens >= 11 && tens <= 13
      ? 'th'
      : ones === 1
        ? 'st'
        : ones === 2
          ? 'nd'
          : ones === 3
            ? 'rd'
            : 'th';
  return `${String(count)}${suffix}`;
}

// "a", "a and b", "a, b and c".
function listed(words: string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1] ?? ''}`;
}

// How a screen reader names one tick (2 Nov 2026): by its printed number
// when it has one ("0.5", "negative 5"), otherwise by where it is, counted
// from the left ("8th tick from the left"), never by its hidden value.
export function numberLineTickSpoken(line: NumberLineSpec, at: number): string {
  const index = Math.round((at - line.min) / line.step);
  return index % line.labelEvery === 0
    ? numberLineSpoken(line, at)
    : `${ordinal(index + 1)} tick from the left`;
}

// What a screen reader is told in place of the picture (2 Nov 2026): only
// what a sighted child can see. How many ticks there are, the numbers
// printed under them and which ticks they are under, and each marked point
// by its own label and the tick it sits on, counted from the left. Never
// the value of a tick that has no number printed under it, nor the step:
// those are what a question may ask for.
export function describeNumberLine(line: NumberLineSpec): string {
  const count = numberLineTickCount(line);
  const numbers: string[] = [];
  for (let index = 0; index < count; index += line.labelEvery)
    numbers.push(numberLineSpoken(line, line.min + index * line.step));
  const printed =
    line.labelEvery === 1
      ? `Every tick has its number printed under it: ${listed(numbers)}.`
      : `Numbers are printed under every ${ordinal(line.labelEvery)} tick from the left, starting with the 1st: ${listed(numbers)}.`;
  const points = line.marks.map((mark) => {
    const name = numberLineMarkSpoken(mark.label);
    const offset = mark.at - line.min;
    const index = Math.floor(offset / line.step);
    if (offset % line.step !== 0)
      return ` ${name} is between the ${ordinal(index + 1)} and ${ordinal(index + 2)} ticks from the left.`;
    if (index % line.labelEvery === 0)
      return ` ${name} is on the tick numbered ${numberLineSpoken(line, mark.at)}.`;
    return ` ${name} is on the ${ordinal(index + 1)} tick from the left.`;
  });
  // Year 8: a line showing an inequality, with every tick numbered, opens
  // with the approved words, "A number line from negative 6 to 4."
  const inequality = describeNumberLineInequality(line);
  const opening =
    inequality !== null && line.labelEvery === 1
      ? `A number line from ${numberLineSpoken(line, line.min)} to ${numberLineSpoken(line, line.max)}.`
      : `A number line with ${String(count)} ticks. ${printed}`;
  return `${opening}${inequality === null ? '' : ` ${inequality}`}${points.join('')}`;
}

// "a filled dot at negative 2", "an open dot at 5". A dot on a tick with no
// number printed under it is placed by counting ticks, as marked points
// are, so its hidden value is never said.
function numberLineDotSpoken(line: NumberLineSpec, dot: NumberLineDot): string {
  const index = (dot.at - line.min) / line.step;
  const where =
    index % line.labelEvery === 0
      ? numberLineSpoken(line, dot.at)
      : `the ${ordinal(index + 1)} tick from the left`;
  return `${dot.dot === 'open' ? 'an open' : 'a filled'} dot at ${where}`;
}

function capitalised(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// The approved words for an inequality (2 Nov 2026): only the dots, what
// kind they are and where the thick line goes. Never "greater than", "less
// than" or "includes": reading that is the question.
export function describeNumberLineInequality(
  line: NumberLineSpec,
): string | null {
  if (line.ray) {
    return `${capitalised(numberLineDotSpoken(line, line.ray))}, with an arrow going ${line.ray.arrow} from it.`;
  }
  if (line.segment) {
    return `${capitalised(numberLineDotSpoken(line, line.segment.from))} and ${numberLineDotSpoken(line, line.segment.to)}, joined by a thick line.`;
  }
  return null;
}

// One dot as the question bank writes it, before the line's parts are
// known: { at, dot: "open" | "filled" }. Null for anything else.
type RawDot = { place: Exact; dot: 'open' | 'filled' };
function readRawDot(value: unknown): RawDot | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return null;
  const record = value as Record<string, unknown>;
  const place = readExact(record.at);
  if (place === null || (record.dot !== 'open' && record.dot !== 'filled'))
    return null;
  return { place, dot: record.dot };
}

// null when the line is not drawable as given.
export function readNumberLine(
  record: Record<string, unknown>,
): NumberLineSpec | null {
  const step = readExact(record.step);
  const min = readExact(record.min);
  const max = readExact(record.max);
  if (step === null || min === null || max === null) return null;
  const rawMarks: unknown[] =
    record.marks === undefined || record.marks === null
      ? []
      : Array.isArray(record.marks)
        ? (record.marks as unknown[])
        : [null];
  if (rawMarks.length > MAX_MARKS) return null;
  const markRecords: Record<string, unknown>[] = [];
  const markPlaces: Exact[] = [];
  for (const entry of rawMarks) {
    if (typeof entry !== 'object' || entry === null) return null;
    const mark = entry as Record<string, unknown>;
    const at = readExact(mark.at);
    if (at === null) return null;
    markRecords.push(mark);
    markPlaces.push(at);
  }
  // Year 8: a ray { at, dot, arrow } or a segment { from, to }, not both.
  const hasRay = record.ray !== undefined && record.ray !== null;
  const hasSegment = record.segment !== undefined && record.segment !== null;
  if (hasRay && hasSegment) return null;
  let rawRay: { dot: RawDot; arrow: 'left' | 'right' } | null = null;
  let rawSegment: { from: RawDot; to: RawDot } | null = null;
  if (hasRay) {
    const dot = readRawDot(record.ray);
    if (dot === null) return null;
    const arrow = (record.ray as Record<string, unknown>).arrow;
    if (arrow !== 'left' && arrow !== 'right') return null;
    rawRay = { dot, arrow };
  }
  if (hasSegment) {
    if (typeof record.segment !== 'object' || Array.isArray(record.segment))
      return null;
    const segment = record.segment as Record<string, unknown>;
    const from = readRawDot(segment.from);
    const to = readRawDot(segment.to);
    if (from === null || to === null) return null;
    rawSegment = { from, to };
  }
  const dotPlaces = [
    ...(rawRay ? [rawRay.dot.place] : []),
    ...(rawSegment ? [rawSegment.from.place, rawSegment.to.place] : []),
  ];
  const fraction = step.fraction;
  const scale = {
    fraction,
    // A decimal line is counted in its smallest decimal place.
    parts: fraction
      ? step.bottom
      : Math.max(
          ...[step, min, max, ...markPlaces, ...dotPlaces].map((value) =>
            value.fraction ? 1 : value.bottom,
          ),
        ),
  };
  if (
    !fraction &&
    [min, max, ...markPlaces, ...dotPlaces].some((value) => value.fraction)
  ) {
    return null;
  }
  const stepParts = toParts(step, scale.parts);
  const minParts = toParts(min, scale.parts);
  const maxParts = toParts(max, scale.parts);
  if (
    stepParts === null ||
    minParts === null ||
    maxParts === null ||
    stepParts <= 0 ||
    maxParts <= minParts ||
    // The line must end on a tick.
    (maxParts - minParts) % stepParts !== 0 ||
    (maxParts - minParts) / stepParts > MAX_GAPS
  ) {
    return null;
  }
  let labelEvery = 1;
  if (record.labelEvery !== undefined && record.labelEvery !== null) {
    if (
      typeof record.labelEvery !== 'number' ||
      !Number.isInteger(record.labelEvery) ||
      record.labelEvery < 1
    ) {
      return null;
    }
    labelEvery = record.labelEvery;
  }
  const marks: { at: number; label: string }[] = [];
  const taken = new Set<number>();
  for (const [index, mark] of markRecords.entries()) {
    const place = markPlaces[index];
    const at = place ? toParts(place, scale.parts) : null;
    // A letter, a number or "?".
    const label =
      typeof mark.label === 'number'
        ? String(mark.label)
        : typeof mark.label === 'string'
          ? mark.label.trim()
          : '';
    if (
      at === null ||
      at < minParts ||
      at > maxParts ||
      // One point at each place, so a letter names one place.
      taken.has(at) ||
      label.length === 0 ||
      label.length > 3
    ) {
      return null;
    }
    taken.add(at);
    marks.push({ at, label });
  }
  // A dot of an inequality sits on a tick of the line, and never where a
  // lettered point already is.
  const toDot = (raw: RawDot): NumberLineDot | null => {
    const at = toParts(raw.place, scale.parts);
    if (
      at === null ||
      at < minParts ||
      at > maxParts ||
      (at - minParts) % stepParts !== 0 ||
      taken.has(at)
    ) {
      return null;
    }
    return { at, dot: raw.dot };
  };
  let ray: NumberLineRay | null = null;
  if (rawRay) {
    const dot = toDot(rawRay.dot);
    // The arrow needs room: a dot on the end it points to has no arrow.
    if (
      dot === null ||
      (rawRay.arrow === 'left' && dot.at === minParts) ||
      (rawRay.arrow === 'right' && dot.at === maxParts)
    ) {
      return null;
    }
    ray = { ...dot, arrow: rawRay.arrow };
  }
  let segment: NumberLineSegment | null = null;
  if (rawSegment) {
    const from = toDot(rawSegment.from);
    const to = toDot(rawSegment.to);
    // Written left to right, at two different places.
    if (from === null || to === null || from.at >= to.at) return null;
    segment = { from, to };
  }
  return {
    type: 'number_line',
    parts: scale.parts,
    fraction,
    min: minParts,
    max: maxParts,
    step: stepParts,
    labelEvery,
    marks,
    ...(ray ? { ray } : {}),
    ...(segment ? { segment } : {}),
  };
}
